import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { compareStrings } from '../shared/utils';

const initialState = {
  items: [],
  shouldBeUpdated: true,
  lastUpdate: new Date().toISOString(),
  filterFields: { name: '', userId: 'all' },
  order: { by: 'none', direction: 'asc' }
};

// Async thunks
export const loadAccount = createAsyncThunk(
  'financeAccount/loadAccount',
  async (_, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      const filters = getState().financeAccount.filterFields;
      const result = await api.loadAccount(filters);
      dispatch({ type: 'appMain/loadingEnd' });
      return { items: result.items };
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const addAccount = createAsyncThunk(
  'financeAccount/addAccount',
  async (account, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.addAccount(account);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Conta criada com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const updateAccount = createAsyncThunk(
  'financeAccount/updateAccount',
  async (account, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.updateAccount(account);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Conta atualizada com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'financeAccount/deleteAccount',
  async ( id , { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.deleteAccount(id);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Conta excluída com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
      return {};
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

// Create the slice
const financeAccountSlice = createSlice({
  name: 'financeAccount',
  initialState,
  reducers: {
    updateFilters: (state, action) => {
      state.filterFields = { ...state.filterFields, ...action.payload };
      state.shouldBeUpdated = true;
    },
    updateAccountOrder: (state, action) => {
      state.order = action.payload;
      const sortedItems = [...state.items].sort((a, b) => {
        let compareResult = 0;
        
        switch (action.payload.by) {
          case 'name':
            compareResult = compareStrings(a.name, b.name);
            break;
          case 'priority':
            compareResult = compareStrings(a.priority, b.priority);
            break;
          case 'active':
            compareResult = compareStrings(a.active, b.active);
            break;
          case 'user':
            compareResult = compareStrings(a.user?.name, b.user?.name);
            break;
          default:
            compareResult = 0;
        }
        
        return action.payload.direction === 'asc' ? compareResult : -compareResult;
      });
      state.items = sortedItems;
    },
    clear: (state) => {
      state.items = [];
      state.shouldBeUpdated = true;
      state.lastUpdate = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAccount.fulfilled, (state, action) => {
        const { items } = action.payload;
        const sortedItems = [...items].sort((a, b) => {
          let compareResult = 0;
          
          switch (state.order.by) {
            case 'name':
              compareResult = compareStrings(a.name, b.name);
              break;
            case 'type':
              compareResult = compareStrings(a.type, b.type);
              break;
            default:
              compareResult = 0;
          }
          
          return state.order.direction === 'asc' ? compareResult : -compareResult;
        });
        
        state.items = sortedItems;
        state.shouldBeUpdated = false;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(addAccount.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      .addCase(updateAccount.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      });
  }
});

// Export actions
export const { updateFilters, toogleFilters, updateAccountOrder, clear } = financeAccountSlice.actions;

// Export reducer
export default financeAccountSlice.reducer;
